import { ValidateMetadataRequest, LOADER_KEY, CPS_NOTIFICATION_TYPE } from '@cs-cms/types';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useContext } from 'react';
import { useLoaderContext } from '../../contexts/CpsLoaderContext';
import { useGlobalizationContext } from '../../contexts/GlobalizationContext';
import ServicesContext from '../../contexts/ServicesContext';
import { mutateDocumentsKeys } from '../../mutationKeys/documents';
import { useTranslation } from '../globalization/useTranslation';
import { useNotificationContext } from '../useNotificationContext';

export function useValidateMetadata(): UseMutationResult<string[], unknown, { dto: ValidateMetadataRequest }> {
  const servicesCtx = useContext(ServicesContext);
  const { showLoader } = useLoaderContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showToast } = useNotificationContext();

  const validateDocumentMetadata = (data: { dto: ValidateMetadataRequest }): Promise<string[]> => {
    if (servicesCtx?.documentsService && data.dto) {
      showLoader(true, LOADER_KEY.VALIDATE_DOCUMENT_METADATA);
      return servicesCtx.documentsService?.validateDocumentMetadata(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.validateDocumentMetadata,
    mutationFn: (data: { dto: ValidateMetadataRequest }) => validateDocumentMetadata(data),
    onSuccess: errors => {
      if (errors.length > 0) {
        showToast({
          type: CPS_NOTIFICATION_TYPE.ERROR,
          message: errors.join(', ')
        });
      }
    },
    onError: () => {
      showToast({
        type: CPS_NOTIFICATION_TYPE.ERROR,
        message: translate('custom.toolbar.actions.form.metadataValidationFailed')
      });
    },
    onSettled: () => {
      showLoader(false, LOADER_KEY.VALIDATE_DOCUMENT_METADATA);
    }
  });
}
